const article = globalThis.af?.article?.id ?? globalThis.window?.location.pathname.substring(1).split("/")[0];

/**
 * Logs an activity to the website activity log
 *
 * @param activity name of the activity we are logging
 * @param parameters A dictionary of parameters used when doing the activity
 * @param timeElapsed Number of milliseconds elapsed while executing
 */
export async function logActivity(activity: string, parameters: Record<string, any> = {}, timeElapsed = 0) {
	try {
		let response = await fetch("/api/logging/log-activity", {
			method: "POST",
			body: JSON.stringify({
				article,
				timeElapsed,
				action: activity,
				parameters: {
					...parameters,
					_session: getSessionData(),
				},
			}),
			headers: { "Content-Type": "application/json", Accept: "application/json" },
		});

		if (!response.ok) {
			if (response.headers.get("Content-Type")?.includes("application/json")) {
				let data = await response.json();
				console.error(data.error);
			} else {
				console.error(`${response.status} ${response.statusText}`);
			}
		}
	} catch {}
}

/**
 * Records the current time, and returns a function that logs the activity with the
 * time elapsed when it is called.
 *
 * @param activity  Name of the activity we are logging
 * @param parameters A dictionary of parameters used when doing the activity
 * @returns A function that should be called when the activity is completed to log it to the website log
 */
export function startActivity(activity: string, startParameters: Record<string, any> = {}) {
	let start = performance.now();
	return (endParameters: Record<string, any> = {}) =>
		logActivity(activity, { ...startParameters, ...endParameters }, performance.now() - start);
}

/**
 * Records the time used to execute an operation and returns the value of the operation and the time
 * in milliseconds
 */
export async function time<T>(callback: () => T | Promise<T>): Promise<[T, number]> {
	let start = performance.now();
	let returnValue = await callback();

	return [returnValue, performance.now() - start];
}

export async function logError(error: any) {
	try {
		if (!error) return;

		let data =
			typeof error === "string"
				? { error: error }
				: typeof error === "object"
					? "message" in error
						? {
								error: error.message,
								stack: error.stack ?? null,
							}
						: { error: error.toString() }
					: { error: "Unspecified error" };

		console.error(error);

		let result = await fetch("/data/Logger/LogError", {
			method: "POST",
			body: JSON.stringify(data),
			headers: {
				"Content-Type": "application/json; charset=utf-8",
			},
		});

		if (result.ok) {
			return await result.json();
		}

		console.error(Error("Failed to log error"));
	} catch {}
}

function getSessionId() {
	return crypto.randomUUID?.();
}

/** The version in the first URL served under `/lib/@olenbetong/<name>/<version>/`. */
function libVersion(name: string, urls: Iterable<unknown>) {
	let pattern = new RegExp(`/lib/@olenbetong/${name}/([^/]+)/`);
	for (let url of urls) {
		let match = typeof url === "string" ? pattern.exec(url) : null;
		if (match) return match[1];
	}
	return undefined;
}

/**
 * The synergi-chrome and synergi-platform versions the page loads on the 2026 site templates,
 * read from the chrome's script tag and the platform import map rather than from the chrome
 * itself, so they are known even when the chrome failed to start. Empty on other pages.
 */
function getPlatformVersions() {
	let versions: Record<string, string> = {};
	try {
		let importMap = document.querySelector('script[type="importmap"]');
		let platform = importMap
			? libVersion("synergi-platform", Object.values(JSON.parse(importMap.textContent ?? "{}").imports ?? {}))
			: undefined;
		if (platform) versions["synergi-platform"] = platform;
	} catch {}
	let chrome =
		libVersion(
			"synergi-chrome",
			Array.from(document.scripts, (script) => script.src),
		) ?? (globalThis as any).ob?.chrome?.version;
	if (chrome) versions["synergi-chrome"] = chrome;
	return versions;
}

let sessionStart = Date.now();
let sessionId = getSessionId();
let deviceId: string | null = "";
let browserSessionId: string | null = "";

if ("document" in globalThis) {
	let startSession = () =>
		logActivity("AfSessionInit", {
			"appframe-core": globalThis.af?.common?.version,
			"appframe-data": (globalThis.af as any)?.data?.version,
			"app-version": globalThis.af?.article?.version,
			...getPlatformVersions(),
		});

	document.addEventListener("DOMContentLoaded", () => {
		deviceId = localStorage.getItem("_afDeviceId");
		browserSessionId = sessionStorage.getItem("_afSessionId");
		if (!deviceId) {
			deviceId = getSessionId();
			localStorage.setItem("_afDeviceId", deviceId);
		}

		if (!browserSessionId) {
			browserSessionId = sessionId;
			sessionStorage.setItem("_afSessionId", browserSessionId);
		}

		startSession();
	});

	document.addEventListener("visibilitychange", function logData() {
		if (document.visibilityState === "hidden") {
			navigator.sendBeacon(
				"/api/logging/log-activity",
				JSON.stringify({
					article,
					action: "AfSessionEnd",
					parameters: {
						_session: getSessionData(),
					},
				}),
			);
		} else {
			startSession();
		}
	});
}

/**
 * Session and device ID's and information about session start and duration.
 * ID's will be null/undefined if crypto.randomUUID() is not supported.
 */
export type SessionData = {
	/**
	 * Random UUID generated on page load, and persisted in sessionStorage
	 */
	browserSessionId: string | null;
	/**
	 * Random UUID generated on page load, and persisted in localStorage
	 */
	deviceId: string | null;
	/**
	 * Time of page load in MS since epoch
	 */
	sessionStart: number;
	/**
	 * Time since page load (in MS) when this method was called
	 */
	sessionLength: number;
	/**
	 * Random UUID generated on page load. Not persisted.
	 */
	sessionId: string | null;
};

export function getSessionData(): SessionData {
	return {
		browserSessionId,
		sessionStart,
		sessionLength: Date.now() - sessionStart,
		sessionId,
		deviceId,
	};
}
